import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const getAuthToken = () => localStorage.getItem('accessToken');

const jsonHeaders = () => ({
  headers: {
    Authorization: `Bearer ${getAuthToken()}`,
    'Content-Type': 'application/json',
  },
});

const handleError = (label, error) => {
  console.error(`${label} error:`, error);
  throw error.response?.data || error;
};

const NotificationAdminService = {
  // ----- Use-case templates -----
  listTemplates: async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/um/admin/v1/notification-templates`, jsonHeaders());
      return res.data;
    } catch (error) {
      handleError('List templates', error);
    }
  },

  getTemplate: async (useCaseCode) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/um/admin/v1/notification-templates/${useCaseCode}`,
        jsonHeaders()
      );
      return res.data;
    } catch (error) {
      handleError('Get template', error);
    }
  },

  upsertTemplate: async (useCaseCode, payload) => {
    try {
      const res = await axios.put(
        `${API_BASE_URL}/um/admin/v1/notification-templates/${useCaseCode}`,
        payload,
        jsonHeaders()
      );
      return res.data;
    } catch (error) {
      handleError('Save template', error);
    }
  },

  toggleTemplate: async (useCaseCode, enabled) => {
    try {
      const res = await axios.patch(
        `${API_BASE_URL}/um/admin/v1/notification-templates/${useCaseCode}/toggle`,
        { enabled },
        jsonHeaders()
      );
      return res.data;
    } catch (error) {
      handleError('Toggle template', error);
    }
  },

  // ----- Mass communication campaigns -----
  listCampaigns: async (page = 0, size = 20) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/um/admin/v1/notification-campaigns?page=${page}&size=${size}`,
        jsonHeaders()
      );
      return res.data;
    } catch (error) {
      handleError('List campaigns', error);
    }
  },

  getCampaign: async (id) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/um/admin/v1/notification-campaigns/${id}`,
        jsonHeaders()
      );
      return res.data;
    } catch (error) {
      handleError('Get campaign', error);
    }
  },

  createCampaign: async (payload) => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/um/admin/v1/notification-campaigns`,
        payload,
        jsonHeaders()
      );
      return res.data;
    } catch (error) {
      handleError('Create campaign', error);
    }
  },

  updateCampaign: async (id, payload) => {
    try {
      const res = await axios.put(
        `${API_BASE_URL}/um/admin/v1/notification-campaigns/${id}`,
        payload,
        jsonHeaders()
      );
      return res.data;
    } catch (error) {
      handleError('Update campaign', error);
    }
  },

  deleteCampaign: async (id) => {
    try {
      const res = await axios.delete(
        `${API_BASE_URL}/um/admin/v1/notification-campaigns/${id}`,
        jsonHeaders()
      );
      return res.data;
    } catch (error) {
      handleError('Delete campaign', error);
    }
  },

  previewRecipientCount: async (payload) => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/um/admin/v1/notification-campaigns/preview-count`,
        payload,
        jsonHeaders()
      );
      return res.data;
    } catch (error) {
      handleError('Preview recipients', error);
    }
  },

  sendCampaign: async (id) => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/um/admin/v1/notification-campaigns/${id}/send`,
        {},
        jsonHeaders()
      );
      return res.data;
    } catch (error) {
      handleError('Send campaign', error);
    }
  },

  // ----- Teams (for targeting autocomplete) -----
  listTeams: async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/em/v1/admin/teams`, jsonHeaders());
      return res.data;
    } catch (error) {
      handleError('List teams', error);
    }
  },

  // ----- Notification Categories -----
  listCategories: async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/um/admin/v1/notification-categories`, jsonHeaders());
      return res.data;
    } catch (error) {
      handleError('List categories', error);
    }
  },

  listEnabledCategories: async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/um/admin/v1/notification-categories/enabled`, jsonHeaders());
      return res.data;
    } catch (error) {
      handleError('List enabled categories', error);
    }
  },

  getCategory: async (id) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/um/admin/v1/notification-categories/${id}`, jsonHeaders());
      return res.data;
    } catch (error) {
      handleError('Get category', error);
    }
  },

  createCategory: async (payload) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/um/admin/v1/notification-categories`, payload, jsonHeaders());
      return res.data;
    } catch (error) {
      handleError('Create category', error);
    }
  },

  updateCategory: async (id, payload) => {
    try {
      const res = await axios.put(`${API_BASE_URL}/um/admin/v1/notification-categories/${id}`, payload, jsonHeaders());
      return res.data;
    } catch (error) {
      handleError('Update category', error);
    }
  },

  deleteCategory: async (id) => {
    try {
      const res = await axios.delete(`${API_BASE_URL}/um/admin/v1/notification-categories/${id}`, jsonHeaders());
      return res.data;
    } catch (error) {
      handleError('Delete category', error);
    }
  },

  // ----- Shared image upload (reuses the content image endpoint) -----
  uploadImage: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'notification-images');
      const res = await axios.post(`${API_BASE_URL}/em/v1/admin/images/upload`, formData, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      return res.data?.data?.imageUrl;
    } catch (error) {
      handleError('Upload image', error);
    }
  },
};

export default NotificationAdminService;
